import { Inject, Injectable } from '@angular/core';
import { Observable, from, of, tap, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';

import { Subject, filter, takeUntil, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  AccountInfo,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { AssetsDataService } from './assets-data.service';
import { ToastService } from './toast.service';
import { User } from '../models/User.interface';

/**
 * Service for handling MSAL authentication and token management.
 * This service manages user authentication, login, logout, token acquisition, and session handling using MSAL Angular.
 * It communicates with the MSAL library to acquire tokens, handle login states, and set user information.
 *
 * The service controls the following `BehaviorSubject` observables:
 * - `userName`: Tracks and updates the user's name.
 *    Other components can subscribe to this observable to get the latest user name automatically.
 * - `userIsLoggedIn`: Tracks the user's login status (true/false).
 *    Components that subscribe to this observable will be notified when the login status changes.
 *
 * This service updates these `BehaviorSubject`s based on MSAL events and provides methods for login, logout, and token management,
 * ensuring that other components in the app react to changes in user authentication state and details automatically.
 *
 * The method `msalInstanceInit` is called by the APP_INITIALIZER in app.module.ts
 * to ensure that the MSAL instance is initialized and the user's login status is checked when the app starts.
 */

@Injectable({
  providedIn: 'root',
})
export class EntraService {
  // BehaviorSubjects to manage and share state across components
  public user: BehaviorSubject<User> = new BehaviorSubject<User>({
    email: '',
    name: '',
    isAdmin: false,
  });

  public userIsLoggedIn = new BehaviorSubject<boolean>(false);

  public UserName!: string;
  public UserEmail!: string;
  public UserIsLoggedIn: boolean = false;

  public tokenExpiration!: number;

  private _msalGuardConfig: MsalGuardConfiguration;
  private _msalService: MsalService;
  private _msalBroadcastService: MsalBroadcastService;

  private _accessToken: string | undefined;

  // Set once we've handled an unrecoverable session on load, so the app-init
  // subscription (which fires repeatedly) doesn't toast/redirect more than once.
  private _deadSessionHandled = false;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private assetsService: AssetsDataService,
    private _router: Router,
    private _toastService: ToastService
  ) {
    this._msalService = msalService;
    this._msalGuardConfig = msalGuardConfig;
    this._msalService = msalService;
    this._msalBroadcastService = msalBroadcastService;
  }

  /**
   * Acquires an access token either from the cache or by calling MSAL's acquireTokenSilently method.
   * If a cached token exists and is not expired, it is returned. Otherwise, a new token is fetched silently.
   * @returns Observable of the access token or undefined if an error occurs.
   */
  public acquireToken(): Observable<string | undefined> {
    // Check if the user is logged in, if not, redirect to logout page
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      // If no active account, redirect to logout page
      console.log('User is not logged in.');
      return of(undefined);
    }

    // Check if the cached token is expired, if not, return it
    //If the token is expired, acquire a new one via acquireTokenSilently
    const tokenIsExpired = this.isTokenExpired(this.tokenExpiration);
    if (
      this._accessToken &&
      this._accessToken !== undefined &&
      !tokenIsExpired
    ) {
      return of(this._accessToken); // Return cached token as an observable
    } else {
      // Call acquireTokenSilently and return the observable
      console.log(
        'calling acquireToken for an API, token is expired or not available'
      );
      const tTokenStart = performance.now();
      return this.acquireTokenSilently(environment.apiScope).pipe(
        tap((accessToken) => {
          console.log(
            `[entra] acquireTokenSilent took ${Math.round(performance.now() - tTokenStart)}ms — token ${accessToken ? 'OK' : 'UNDEFINED'}`
          );
          this.updateLocalToken(accessToken as string);
        }),
        switchMap((accessToken) => {
          // Return the token after storing it, wrapped in an observable
          return of(accessToken);
        }),
        catchError((err) => {
          console.error('Error acquiring token:', err);
          return of(undefined); // Return undefined in case of error
        })
      );
    }
  }

  /**
   * Initializes MSAL instance by checking login status and setting user information.
   * It subscribes to MSAL's broadcast service to check for changes in login status.
   * If a user is logged in, their information is set and the user is activated.
   *  The `userIsLoggedIn` BehaviorSubject will be updated and any components or services
   * that are subscribed to this observable will be automatically notified of the update.
   *
   *
   * @returns A promise that resolves when the initialization is complete.
   */
  public msalInstanceInit(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._msalBroadcastService.inProgress$
        .pipe(
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None
          ),
          takeUntil(this._destroying$)
        )
        .subscribe(() => {
          const loggedIn =
            this._msalService.instance.getAllAccounts().length > 0;

          // Order matters: `userIsLoggedIn` is a BehaviorSubject, so `.next(true)`
          // runs every parked subscriber synchronously. Services that gate on it
          // (ContactsService.ensureCompanyEmails) immediately call acquireToken(),
          // which reads getActiveAccount(). Publishing before the active account
          // is set hands them a null account and a `Bearer undefined` request.
          if (loggedIn) {
            let user: AccountInfo =
              this._msalService.instance.getAllAccounts()[0];
            this._msalService.instance.setActiveAccount(user);
            this.SetUser(user);
          }

          this.userIsLoggedIn.next(loggedIn);
        });
      resolve();
    });
  }

  /**
   * Cleans up resources by completing the destroying subject when the component is destroyed.
   * This ensures that any ongoing subscriptions or processes are properly disposed of.
   */
  msalInstanceNgOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  /**
   * Initiates a login process using MSAL's loginRedirect method.
   * If there is a predefined authentication request, it is passed; otherwise, the default login flow is used.
   *
   * This method is called via methods in the app component as well as the landing page component.
   */
  public login() {
    if (this._msalGuardConfig.authRequest) {
      this._msalService.loginRedirect({
        ...this._msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this._msalService.loginRedirect();
    }
  }

  /**
   * Logs out the current user and resets session data, including user details and authentication state.
   * After logout, the user is redirected to the configured logout redirect URL.
   */
  public logout() {

    this.userIsLoggedIn.next(false);
    this.user.next({email: '', name: '', isAdmin: false});
    this.UserIsLoggedIn = false;
    this.UserName = '';
    this.UserEmail = '';
    this._msalService.logoutRedirect();
  }

  /**
   * Sets user details, processes their app-role assignments, and attempts to acquire an access token if the user has the required access.
   *
   * - Formats the user's name into "First Last" format if it's provided as "Last, First".
   * - Checks if the user has been assigned the required app role and stores this information in localStorage for persistence.
   * - Checks if the user is an admin by checking for the admin app role in the token.
   * - If the user has the necessary access, it attempts to acquire an access token silently to authenticate the user for API calls.
   *
   * @param user The account information returned from MSAL after login, which includes user details, roles, and other claims.
   */

  public async SetUser(user: AccountInfo) {
    //**Getting and setting the user's access status:
    // Retrieve the 'roles' claim from the user's ID token (array of app-role value strings),
    // or default to an empty array if the claim is absent (no roles assigned).
    const roles: string[] = (user.idTokenClaims?.['roles'] as string[]) ?? [];
console.log('User', user);
// Check if the user has been assigned the required admin app role (defined in the environment configuration)
        const userIsAdmin = roles.includes(environment.AppRoleAdmin);
    // Check if the user has been assigned the required general-access app role (defined in the environment configuration)
    const userHasAccess = roles.includes(environment.AppRoleUser) || userIsAdmin;

// console.log('User roles:', roles);
// console.log('User is admin:', userIsAdmin);
// console.log('User has access:', userHasAccess);
    // Store the user's access status (true/false) in localStorage for persistence across sessions
    localStorage.setItem('userHasAccess', JSON.stringify(userHasAccess));

    // Check if the user has been assigned the admin app role (defined in the environment configuration)


    //**Setting the User's Name and Email:
    // Temporarily store the user's name for processing
    const tempName = user.name as string;
    let userName = '';

    // Split the name into parts, assuming the format "Last, First" (comma-separated)
    const parts = tempName.split(',').map((part) => part.trim());

    // If the name does not have a comma (i.e., it is a single part), set the name as-is
    if (parts.length !== 2) {
      userName = tempName;
    }
    // If the name has two parts (Last, First format), reformat it into "First Last" format
    else {
      const [last, first] = parts;
      userName = `${first} ${last}`;
    }
    this.UserName = userName;

    // Store the user's email (or username) in the service for future reference
    const userEmail = user.username;
    this.UserEmail = userEmail;

    // Create a User object with the user's email and name
    const userObj: User = {
      email: userEmail,
      name: userName,
      isAdmin: userIsAdmin,
    };

    //testing user without admin access
    // const userObj: User = {
    //   email: userEmail,
    //   name: userName,
    //   isAdmin: false
    // };

    this.user.next(userObj);

    console.log('user', userObj);

    // **If the user has access to the required group, proceed to acquire an access token

    if (userHasAccess) {
      // Attempt to acquire an access token silently using the MSAL service
      console.log('User has access. Attempting to acquire token silently...');
      this.acquireTokenSilently(environment.apiScope, user).subscribe({
        next: (accessToken) => {
          if (accessToken) {
            // Token acquired — store it for API requests.
            this.updateLocalToken(accessToken);
          } else {
            // The user looks signed in but the session can no longer produce a
            // token (refresh token expired/revoked). acquireTokenSilently
            // swallows the failure and emits undefined, so this is our signal
            // to log them out and prompt a fresh login.
            this.handleUnrecoverableSession();
          }
        },
        // acquireTokenSilently already catches errors and emits undefined, so
        // this is only for truly unexpected stream errors — treat the same way.
        error: (err) => {
          console.error('Error acquiring token via SetUser:', err);
          this.handleUnrecoverableSession();
        },
        complete: () => {
          console.log('Token acquisition process completed.');
        },
      });
    }
  }

  /**
   * Handles a session that is signed in but can no longer produce a token
   * (silent acquisition failed on load — refresh token expired/revoked).
   *
   * Rather than leaving the app running with no usable token, this clears the
   * local session, drops MSAL's stale cache so a reload doesn't loop back here,
   * tells the user via a toast, and sends them to the landing page to sign in
   * again. Guarded so it only runs once per load.
   */
  private async handleUnrecoverableSession(): Promise<void> {
    if (this._deadSessionHandled) {
      return;
    }
    this._deadSessionHandled = true;

    // Reset local auth state (RoleGuard reads userHasAccess from localStorage).
    this.userIsLoggedIn.next(false);
    this.user.next({ email: '', name: '', isAdmin: false });
    this.UserIsLoggedIn = false;
    this.UserName = '';
    this.UserEmail = '';
    this._accessToken = undefined;
    localStorage.removeItem('userHasAccess');

    // Drop MSAL's stale cached account/tokens (no redirect) so the next load
    // starts clean instead of re-detecting a dead session.
    try {
      await this._msalService.instance.clearCache();
    } catch (err) {
      console.error('Failed to clear MSAL cache on dead session:', err);
    }

    await this._toastService.showWarningMessage(
      'Your session has expired. Please log in again.',
      6000
    );

    this._router.navigate(['/']);
  }

  /**
   * Checks if the provided JWT token has expired by decoding it and comparing the expiration time to the current time.
   * @param token - The JWT token to check for expiration.
   * @returns Boolean indicating whether the token has expired.
   */
  private isTokenExpired(tokenExpiration: number): boolean {
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    return tokenExpiration < currentTime;
  }

  /**
   * Updates the local access token and its expiration time by decoding the JWT token.
   * @param token - The JWT token to update the local access token and expiration time.
   * */
  private updateLocalToken(token: string): void {
    // A silent-acquire failure emits undefined (see acquireTokenSilently's
    // catchError). Don't poison the cache with it — leaving the previous token
    // (or none) in place lets the next call retry cleanly instead of every
    // request missing the cache and re-acquiring.
    if (!token) { return; }
    this._accessToken = token;
    try {
      const decoded: any = jwtDecode(token);
      this.tokenExpiration = decoded.exp;
    } catch {
      // Undecodable token: force a re-acquire next time rather than caching a
      // bad expiry (an undefined expiry reads as "not expired").
      this.tokenExpiration = 0;
    }
  }

  /**
   * Acquires an access token, either from the cache or by silently requesting a new one.
   *
   * The function first checks if there is an active user account. If no active account is found,
   * it redirects the user to the logout page. If an active account exists, it checks if the cached
   * token is still valid by verifying its expiration.
   *
   * - If a valid, non-expired cached token exists, it returns the token as an observable.
   * - If the cached token is expired or does not exist, it silently acquires a new token from the Microsoft Identity platform.
   * - The newly acquired token is stored for future use and returned as an observable.
   *
   * If any error occurs during the token acquisition process, `undefined` is returned.
   *
   * @returns Observable<string | undefined> - The access token as an observable or `undefined` in case of error.
   */
  public acquireTokenSilently(
    scopes: string[],
    user?: AccountInfo
  ): Observable<string | undefined> {
    const activeAccount = user
      ? user
      : this._msalService.instance.getActiveAccount();

    // If there's no active account, return an observable that resolves to null
    if (!activeAccount) {
      console.error('No active account! Please log in.');
      return from(Promise.resolve(undefined)); // Return observable with null
    }

    // Note: `userIsLoggedIn` is published below, only once a token actually
    // comes back. Announcing it on entry marked sessions "logged in" that then
    // failed to produce a token, and every service gated on the subject fired a
    // request on a session that was already dead. The failure path deliberately
    // leaves the subject alone — SetUser's handleUnrecoverableSession() owns it.
    return this._msalService
      .acquireTokenSilent({
        account: activeAccount,
        scopes: scopes,
      })
      .pipe(
        map((response) => {
          // Return the access token if successful
          if (response.accessToken) {
            this.userIsLoggedIn.next(true);
          }
          return response.accessToken;
        }),
        catchError((error) => {
          // Handle error and return null in case of failure
          console.error('Error acquiring token silently', error);
          return from(Promise.resolve(undefined)); // Return observable with null
        })
      );
  }

  // ####################################################################
  //The following method is not currently being used

  /**
   * Checks if the provided access token has expired by decoding the JWT and comparing the expiration time to the current time.
   * @param authResult The authentication result that contains the access token to be checked.
   * @returns Boolean indicating whether the token has expired.
   */
  public isAccessTokenExpired(authResult: AuthenticationResult): boolean {
    const token = authResult.accessToken;

    if (!token) {
      return true; // No token means expired or unavailable
    }

    // Decode JWT to extract payload
    const payload = JSON.parse(atob(token.split('.')[1])); // Base64 decode the payload
    const expirationTime = payload.exp; // `exp` is in seconds since Unix epoch
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds

    // Check if the token has expired
    return currentTime >= expirationTime;
  }
}
